import { CheckCircleSVG, CrossCircleSVG } from "@components/svgs";
import { RenderAnalysisSummarySectionProps } from "@models/platform-analysis/entities/steps/summary";
import { ProductRecommendationTypes } from "@models/platform-analysis/enums/summary/recommendationType";
import { AnalysisSummarySectionId as SectionId } from "@models/platform-analysis/enums/summary/sectionId";
import { theme } from "@themes";
import { hexToRGBA } from "@utils";
import { SummaryAdviserInformation } from "./components/adviser-information";
import { SummaryAppendix } from "./components/appendix";
import { SummaryAppendixBusinessMetricAnalysis } from "./components/appendix-business-metric-analysis";
import { SummaryAppendixFeatureAnalysis } from "./components/appendix-feature-analysis";
import { SummaryAppendixFeeEstimatesAnalysis } from "./components/appendix-fee-estimate";
import { SummaryAppendixPlatformComments } from "./components/appendix-platform-comments";
import { SummaryAppendixSmaPortfolioFees } from "./components/appendix-sma-portfolio-fees";
import { SummaryAppendixPortfolioComposition } from "./components/appendix-portfolio-composition";
import { SummaryBusinessMetrics } from "./components/business-metrics";
import { SummaryConcludingRemarks } from "./components/concluding-remarks";
import { SummaryFeatureAnalysis } from "./components/features";
import { SummaryFeaturesIncluded } from "./components/features-included";
import { SummaryFeeEstimates } from "./components/fee-estimates";
import { SummaryObjective } from "./components/objective";
import { SummaryPlatformComments } from "./components/platform-comments";
import { SummaryPlatformSpecificComments } from "./components/platform-specific-comments";
import { SummaryPlatformsIncluded } from "./components/platforms-included";
import { SummaryPortfolioComposition } from "./components/portfolio-composition";
import { SummaryPortfolioDetails } from "./components/portfolio-details";
import { SummaryProductRecommendations } from "./components/product-recommendations";
import { SummarySmaPortfolioFees } from "./components/sma-portfolio-fees";
import { SummarySupplierInformation } from "./components/supplier-information";
import { SummaryWhatsIncluded } from "./components/whats-included";

export class AnalysisSummaryStaticValueConfigs {
  static readonly MaximumObjectivesCharacters = 3000;
  static readonly MaximumConcludingRemarksCharacters =
    this.MaximumObjectivesCharacters;
  static readonly MaximumFeaturesInDetailView = 300;
  static readonly MaximumCustomRecommendationLabelCharacters = 20;
  static readonly MaximumCharacterComment = 1500;
}

export class AnalysisSummaryStyleConfigs {
  // Feature analysis table
  static readonly DefaultTableHeaderHeight = 100;
  static readonly MaximumShortlistedProductsInTable = 5;
  static readonly DefaultTitleColumnWidth = 250;
  static readonly FeatureLevelTitleColumnWidth = 360;
  static readonly DefaultProductColumnWidth = 195;
  static readonly AppendixDefaultProductColumnWidth = 139;
  static readonly FeatureLevelProductColumnWidth = 173;
  static readonly AppendixFeatureLevelProductColumnWidth = 144;
  static readonly TotalFeaturesSelectedColumnWidth = 110;
  static readonly DefaultRowHeight = 40;
  static readonly NoSelectionTitleColumnWidth =
    this.DefaultTitleColumnWidth + this.TotalFeaturesSelectedColumnWidth;

  // Portfolio details table
  static readonly PortfolioDetailsTitleColumnWidth =
    this.NoSelectionTitleColumnWidth;
  static readonly PlatformRecommendationTitleColumnWidth =
    this.NoSelectionTitleColumnWidth;

  // Portfolio composition table
  static readonly PortfolioCompositionNumOfItemsInTable = 6;
  static readonly PortfolioCompositionTitleColumnWidth = 315;
  static readonly PortfolioCompositionDefaultColumnWidth = 170;
}

export const AnalysisSummaryStepSectionRenderConfigs: RenderAnalysisSummarySectionProps[] =
  [
    {
      id: SectionId.SupplierInformation,
      showFor: ["supplier"],
      Component: SummarySupplierInformation,
    },
    {
      id: SectionId.AdviserInformation,
      showFor: ["adviser"],
      Component: SummaryAdviserInformation,
    },
    {
      id: SectionId.Objective,
      showFor: ["supplier", "adviser"],
      name: {
        supplier: "Objectives",
        adviser: "Objectives",
      },
      Component: SummaryObjective,
    },
    {
      id: SectionId.WhatsIncluded,
      showFor: ["adviser"],
      name: {
        adviser: "What's included",
      },
      Component: SummaryWhatsIncluded,
    },
    {
      id: SectionId.ProductRecommendations,
      showFor: ["adviser"],
      name: { adviser: "Platform recommendations" },
      Component: SummaryProductRecommendations,
    },
    {
      id: SectionId.PlatformSpecificComments,
      showFor: ["adviser"],
      Component: SummaryPlatformSpecificComments,
    },
    {
      id: SectionId.FeatureAnalysis,
      showFor: ["supplier", "adviser"],
      name: {
        supplier: "Feature analysis",
        adviser: "Feature review",
      },
      Component: SummaryFeatureAnalysis,
      dependencyField: "hasFeatureStep",
    },
    {
      id: SectionId.BusinessMetricsAnalysis,
      showFor: ["supplier", "adviser"],
      name: {
        supplier: "Business metrics",
        adviser: "Business metrics",
      },
      Component: SummaryBusinessMetrics,
      dependencyField: "hasBusinessMetricStep",
    },
    {
      id: SectionId.PortfolioDetails,
      name: {
        supplier: "Portfolio details for fee estimates",
        adviser: "Portfolio details for fee estimates",
      },
      showFor: ["supplier", "adviser"],
      Component: SummaryPortfolioDetails,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.PortfolioComposition,
      name: {
        supplier: "Portfolio composition by platform",
        adviser: "Portfolio composition by platform",
      },
      showFor: ["supplier", "adviser"],
      Component: SummaryPortfolioComposition,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.FeeEstimates,
      name: {
        supplier: "Fee estimates",
        adviser: "Fee estimates",
      },
      showFor: ["supplier", "adviser"],
      Component: SummaryFeeEstimates,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.SmaPortfolioFees,
      name: {
        supplier: "SMA portfolio fees by platform",
        adviser: "SMA portfolio fees by platform",
      },
      showFor: ["supplier", "adviser"],
      Component: SummarySmaPortfolioFees,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.ConcludingRemarks,
      name: {
        supplier: "Concluding remarks",
        adviser: "Concluding remarks",
      },
      showFor: ["supplier", "adviser"],
      Component: SummaryConcludingRemarks,
    },
    {
      id: SectionId.PlatformIncluded,
      showFor: ["supplier"],
      Component: SummaryPlatformsIncluded,
    },
    {
      id: SectionId.PlatformComments,
      showFor: ["supplier"],
      Component: SummaryPlatformComments,
    },
    {
      id: SectionId.Appendix,
      showFor: ["adviser"],
      Component: SummaryAppendix,
    },
    {
      id: SectionId.AppendixPlatformComments,
      showFor: ["adviser"],
      Component: SummaryAppendixPlatformComments,
    },
    {
      id: SectionId.AppendixFeatureAnalysis,
      showFor: ["adviser"],
      Component: SummaryAppendixFeatureAnalysis,
      dependencyField: "hasFeatureStep",
    },
    {
      id: SectionId.AppendixBusinessMetricAnalysis,
      showFor: ["adviser"],
      Component: SummaryAppendixBusinessMetricAnalysis,
      dependencyField: "hasBusinessMetricStep",
    },
    {
      id: SectionId.AppendixPortfolioComposition,
      name: {
        adviser: "Portfolio composition by platform",
      },
      showFor: ["adviser"],
      Component: SummaryAppendixPortfolioComposition,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.AppendixFeeEstimateAnalysis,
      showFor: ["adviser"],
      Component: SummaryAppendixFeeEstimatesAnalysis,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.AppendixSmaPortfolioFees,
      showFor: ["adviser"],
      Component: SummaryAppendixSmaPortfolioFees,
      dependencyField: "hasFeeStep",
    },
    {
      id: SectionId.FeatureIncluded,
      showFor: ["supplier", "adviser"],
      Component: SummaryFeaturesIncluded,
      dependencyField: "hasFeatureStep",
    },
  ];

export const AnalysisSummaryRecommendationLabelConfigs: Record<
  ProductRecommendationTypes,
  { backgroundColor: string; icon?: JSX.Element }
> = {
  [ProductRecommendationTypes.Retain]: {
    backgroundColor: theme.palette.success.light,
    icon: <CheckCircleSVG />,
  },
  [ProductRecommendationTypes.Recommended]: {
    backgroundColor: theme.palette.success.light,
    icon: <CheckCircleSVG />,
  },
  [ProductRecommendationTypes.Alternative]: {
    backgroundColor: hexToRGBA(theme.palette.secondary.light, 0.8),
  },
  [ProductRecommendationTypes.NotRecommended]: {
    backgroundColor: theme.palette.text.disabled,
    icon: <CrossCircleSVG />,
  },
  [ProductRecommendationTypes.Replace]: {
    backgroundColor: theme.palette.text.disabled,
    icon: <CrossCircleSVG />,
  },
  [ProductRecommendationTypes.Custom]: {
    backgroundColor: hexToRGBA(theme.palette.primary.light, 0.8),
  },
};
